<template>
    <div class="row banner-text">
        <div class="col-lg-6 offset-lg-3 col-xs-12">
            <div class="card">
                <div class="card-body">
                    <validation-observer
                        slim
                        ref="form"
                        v-slot="{ handleSubmit, invalid }"
                    >
                        <b-form
                            id="capilar-form"
                            @submit.prevent="handle(handleSubmit, invalid)"
                            novalidate
                        >
                            <h1 class="text-center">
                                {{ $t("pages.forms.titles.capilar") }}
                            </h1>

                            <b-alert
                                v-model="showTopBannerForSeconds"
                                class="position-fixed fixed-top m-0 rounded-0"
                                style="z-index: 2000;"
                                variant="danger"
                                dismissible
                            >
                                <h4 class="mb-0">
                                    <span
                                        class="text-danger font-weight-bolder font-italic"
                                        >{{
                                            $t(
                                                "pages.forms.alerts.red-fields.banner-text"
                                            )
                                        }}</span
                                    >
                                </h4>
                            </b-alert>

                            <b-alert variant="info" :show="inReview">
                                <h4>
                                    <i class="fas fa-bell" />
                                    {{ $t("pages.forms.alerts.review.title") }}
                                </h4>
                                <p>
                                    {{
                                        $t(
                                            "pages.forms.alerts.review.description"
                                        )
                                    }}
                                </p>
                            </b-alert>

                            <h2 id="form-start">
                                {{ $t("pages.forms.headers.sample") }}
                            </h2>

                            <sample-id
                                v-model="form.sampleID"
                                required
                                :reviewing="inReview"
                            />

                            <sent-date
                                v-model="form.sentDate"
                                required
                                :reviewing="inReview"
                            />

                            <h2>{{ $t("pages.forms.headers.general") }}</h2>

                            <gender
                                v-model="form.gender"
                                required
                                :in-review="inReview"
                            />
                            <birth-date
                                v-model="form.birthdate"
                                required
                                :reviewing="inReview"
                            />
                            <height
                                v-model="form.height"
                                required
                                :reviewing="inReview"
                            />
                            <weight
                                v-model="form.weight"
                                required
                                :reviewing="inReview"
                            />

                            <h2>
                                {{
                                    $t(
                                        "pages.forms.headers.capilar.family-diseases"
                                    )
                                }}
                            </h2>

                            <yes-no-question
                                required
                                id="caffeineSensitivity"
                                v-model="form.caffeineSensitivity"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.caffeine-sensitivity"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <drug-abbuse
                                v-model="form.drugAbbuse"
                                required
                                :reviewing="inReview"
                            />

                            <alopecia-type
                                v-model="form.alopeciaType"
                                required
                                :reviewing="inReview"
                            />

                            <familiar-history
                                v-model="form.familiarHistory"
                                required
                                :reviewing="inReview"
                            />

                            <hair-loss
                                v-model="form.hairLoss"
                                required
                                :reviewing="inReview"
                            />

                            <med-consumption
                                v-model="form.medConsumption"
                                required
                                :reviewing="inReview"
                            />

                            <capilar-diseases
                                v-model="form.diseases"
                                required
                                :reviewing="inReview"
                            />

                            <eating-disorders
                                v-model="form.eatingDisorders"
                                required
                                :reviewing="inReview"
                            />

                            <yes-no-question
                                required
                                id="hypocaloricDiet"
                                v-model="form.hypocaloricDiet"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.hypocaloric-diet"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <capilar-allergies
                                v-model="form.allergies"
                                required
                                :reviewing="inReview"
                            />

                            <yes-no-question
                                required
                                id="finasDutasSensitivity"
                                v-model="form.finasDutasSensitivity"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.hypersensitivity-finasteride-dutasteride"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="testosteroneConsumption"
                                v-model="form.testosteroneConsumption"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.testosterone-consumption"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <div v-if="isFemale">
                                <yes-no-question
                                    required
                                    id="pregnant"
                                    v-model="form.pregnant"
                                    :in-review="inReview"
                                >
                                    <template slot="label"
                                        >{{ $t("pages.forms.capilar.pregnant")
                                        }}<span :class="requiredClasses"
                                            >*</span
                                        ></template
                                    >
                                </yes-no-question>
                            </div>

                            <h2>
                                {{
                                    $t(
                                        "pages.forms.headers.capilar.environment"
                                    )
                                }}
                            </h2>

                            <yes-no-question
                                required
                                id="stress"
                                v-model="form.stress"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.stress")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="residenceChange"
                                v-model="form.residenceChange"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.residence-change"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="depression"
                                v-model="form.depression"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.depression")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="rest"
                                v-model="form.rest"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.rest")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="childRecently"
                                v-model="form.childRecently"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.child-recently"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <yes-no-question
                                required
                                id="postSurgicalStress"
                                v-model="form.postSurgicalStress"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.post-surgical-stress"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <div v-if="isFemale">
                                <yes-no-question
                                    required
                                    id="irregularPeriods"
                                    v-model="form.irregularPeriods"
                                    :in-review="inReview"
                                >
                                    <template slot="label"
                                        >{{
                                            $t(
                                                "pages.forms.capilar.irregular-periods"
                                            )
                                        }}<span :class="requiredClasses"
                                            >*</span
                                        ></template
                                    >
                                </yes-no-question>

                                <yes-no-question
                                    required
                                    id="abundantPeriods"
                                    v-model="form.abundantPeriods"
                                    :in-review="inReview"
                                >
                                    <template slot="label"
                                        >{{
                                            $t(
                                                "pages.forms.capilar.abundant-periods"
                                            )
                                        }}<span :class="requiredClasses"
                                            >*</span
                                        ></template
                                    >
                                </yes-no-question>
                            </div>

                            <yes-no-question
                                required
                                id="contactToxicSubstances"
                                v-model="form.contactToxicSubstances"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.contact-toxic-substances"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <h2>
                                {{
                                    $t(
                                        "pages.forms.headers.capilar.last-analytics"
                                    )
                                }}
                            </h2>

                            <high-med-low-question
                                required
                                id="cholesterol"
                                v-model="form.cholesterol"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.cholesterol-levels"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <high-med-low-question
                                required
                                id="hemoglobin"
                                v-model="form.hemoglobin"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.hemoglobin")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <high-med-low-question
                                required
                                id="hematocrit"
                                v-model="form.hematocrit"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.hematocrit")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <high-med-low-question
                                required
                                id="redBloodCells"
                                v-model="form.redBloodCells"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.red-blood-cells"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <high-med-low-question
                                required
                                id="tsh"
                                v-model="form.tsh"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.tsh")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <high-med-low-question
                                required
                                id="t3"
                                v-model="form.t3"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.t3")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <high-med-low-question
                                required
                                id="t4"
                                v-model="form.t4"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{ $t("pages.forms.capilar.t4")
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </high-med-low-question>

                            <h2>
                                {{
                                    $t(
                                        "pages.forms.headers.capilar.clinical-inspection"
                                    )
                                }}
                            </h2>

                            <capilar-products
                                v-model="form.products"
                                required
                                :reviewing="inReview"
                            />

                            <hair-size
                                v-model="form.hairSize"
                                required
                                :in-review="inReview"
                            />

                            <pull-test
                                v-model="form.pullTest"
                                required
                                :reviewing="inReview"
                            />

                            <hair-wash
                                v-model="form.hairWash"
                                required
                                :in-review="inReview"
                            />

                            <scalp-condition
                                v-model="form.scalpCondition"
                                required
                                :reviewing="inReview"
                            />

                            <yes-no-question
                                required
                                id="alopecicPlates"
                                v-model="form.alopecicPlates"
                                :in-review="inReview"
                            >
                                <template slot="label"
                                    >{{
                                        $t(
                                            "pages.forms.capilar.alopecic-plates"
                                        )
                                    }}<span :class="requiredClasses"
                                        >*</span
                                    ></template
                                >
                            </yes-no-question>

                            <hair-loss-other-parts
                                v-model="form.hairLossOtherParts"
                                required
                                :reviewing="inReview"
                            />

                            <current-hair-treatment
                                v-model="form.currentHairTreatment"
                                required
                                :reviewing="inReview"
                            />

                            <past-hair-treatment
                                v-model="form.pastHairTreatment"
                                required
                                :reviewing="inReview"
                            />

                            <capilar-gdpr-checkbox
                                v-model="conditionsAccepted"
                            />

                            <b-button-toolbar
                                style="justify-content: center; display: flex;"
                            >
                                <b-button-group v-show="inReview" class="mx-1">
                                    <b-button
                                        type="button"
                                        :disabled="alreadySent"
                                        @click.prevent="inReview = false"
                                        >{{ backButtonText }}</b-button
                                    >
                                </b-button-group>
                                <b-button-group class="mx-1">
                                    <b-button
                                        id="sendBtn"
                                        type="submit"
                                        variant="info"
                                        style="color: white;"
                                        :disabled="
                                            !conditionsAccepted || alreadySent
                                        "
                                    >
                                        <b-spinner
                                            small
                                            type="border"
                                            v-show="showSpinner"
                                        />
                                        {{ buttonText }}
                                    </b-button>
                                </b-button-group>
                            </b-button-toolbar>
                        </b-form>
                    </validation-observer>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import VueScrollTo from "vue-scrollto";
import FormInputHelpers from "../../mixins/FormInputHelpers";
import axios from "axios";
import { ValidationObserver } from "vee-validate";
import SampleId from "../../components/form-components/sample/SampleId";

export default {
    mixins: [FormInputHelpers],
    components: { SampleId, ValidationObserver },
    data() {
        return {
            form: {
                sampleID: "",
                sentDate: "",
                gender: "",
                birthdate: "",
                height: "",
                weight: "",
                caffeineSensitivity: "",
                drugAbbuse: [],
                alopeciaType: null,
                familiarHistory: null,
                hairLoss: null,
                medConsumption: [],
                diseases: [],
                eatingDisorders: [],
                hypocaloricDiet: "",
                allergies: [],
                finasDutasSensitivity: "",
                testosteroneConsumption: "",
                pregnant: null,
                stress: "",
                residenceChange: "",
                depression: "",
                rest: "",
                childRecently: "",
                postSurgicalStress: "",
                irregularPeriods: null,
                abundantPeriods: null,
                contactToxicSubstances: "",
                cholesterol: null,
                hemoglobin: null,
                hematocrit: null,
                redBloodCells: null,
                tsh: null,
                t3: null,
                t4: null,
                products: [],
                hairSize: null,
                pullTest: null,
                hairWash: null,
                scalpCondition: [],
                alopecicPlates: "",
                hairLossOtherParts: [],
                currentHairTreatment: "",
                pastHairTreatment: ""
            },
            conditionsAccepted: false,
            inReview: false,
            showSpinner: false,
            alreadySent: false,
            showTopBannerForSeconds: false,
            isFemale: false,
            buttonText: this.$t("pages.forms.button-texts.next"),
            backButtonText: this.$t("pages.forms.button-texts.back")
        };
    },
    methods: {
        handle(handleSubmit, invalid) {
            if (invalid) {
                this.showTopBannerForSeconds = 5;
            }
            handleSubmit(this.submit);
        },
        trackReview() {
            this.$gtag.event("review-form", {
                event_category: "forms",
                event_label: "ppg-capilar-form"
            });
        },
        trackSend() {
            this.$gtag.event("send-form", {
                event_category: "forms",
                event_label: "ppg-capilar-form"
            });
        },
        trackSampleIdNotFound() {
            this.$gtag.event("sampleid-notfound", {
                event_category: "forms",
                event_label: "ppg-capilar-form"
            });
        },
        trackSampleIdAlreadyRegistered() {
            this.$gtag.event("sampleid-alreadyregistered", {
                event_category: "forms",
                event_label: "ppg-capilar-form"
            });
        },
        trackOtherFail() {
            this.$gtag.event("other-fail", {
                event_category: "forms",
                event_label: "ppg-capilar-form"
            });
        },
        async submit(e) {
            if (!this.inReview) {
                this.inReview = true;
                this.trackReview();
                VueScrollTo.scrollTo(document.getElementById("capilar-form"));
                this.buttonText = this.$i18n.t("pages.forms.button-texts.send");
                return;
            }

            try {
                this.buttonText = this.$i18n.t(
                    "pages.forms.button-texts.sending-data"
                );
                this.showSpinner = true;

                await axios.post(
                    this.route("peoplegenetics.register.capilar_response"),
                    {
                        sampleId: "PGL" + this.form.sampleID,
                        sentDate: this.form.sentDate,
                        gender: this.form.gender,
                        birthdate: this.form.birthdate,
                        height: this.form.height,
                        weight: this.form.weight,
                        caffeineSensitivity: this.form.caffeineSensitivity,
                        drugAbbuse: this.form.drugAbbuse.map(t => t.value),
                        alopeciaType: this.form.alopeciaType.value,
                        familiarHistory: this.form.familiarHistory.value,
                        hairLoss: this.form.hairLoss.value,
                        medConsumption: this.form.medConsumption.map(
                            t => t.value
                        ),
                        diseases: this.form.drugAbbuse.map(t => t.value),
                        eatingDisorders: this.form.eatingDisorders.map(
                            t => t.value
                        ),
                        hypocaloricDiet: this.form.hypocaloricDiet,
                        allergies: this.form.allergies.map(t => t.value),
                        finasDutasSensitivity: this.form.finasDutasSensitivity,
                        testosteroneConsumption: this.form
                            .testosteroneConsumption,
                        pregnant: this.form.pregnant,
                        stress: this.form.stress,
                        residenceChange: this.form.residenceChange,
                        depression: this.form.depression,
                        rest: this.form.rest,
                        childRecently: this.form.childRecently,
                        postSurgicalStress: this.form.postSurgicalStress,
                        irregularPeriods: this.form.irregularPeriods,
                        abundantPeriods: this.form.abundantPeriods,
                        contactToxicSubstances: this.form
                            .contactToxicSubstances,
                        cholesterol: this.form.cholesterol,
                        hemoglobin: this.form.hemoglobin,
                        hematocrit: this.form.hematocrit,
                        redBloodCells: this.form.redBloodCells,
                        tsh: this.form.tsh,
                        t3: this.form.t3,
                        t4: this.form.t4,
                        products: this.form.products.map(t => t.value),
                        hairSize: this.form.hairSize,
                        pullTest: this.form.pullTest.value,
                        hairWash: this.form.hairWash,
                        scalpCondition: this.form.scalpCondition.map(
                            t => t.value
                        ),
                        alopecicPlates: this.form.alopecicPlates,
                        hairLossOtherParts: this.form.hairLossOtherParts.map(
                            t => t.value
                        ),
                        currentHairTreatment: this.form.currentHairTreatment,
                        pastHairTreatment: this.form.pastHairTreatment
                    }
                );

                this.trackSend();
                this.alreadySent = true;

                this.$swal({
                    title: this.$i18n.t(
                        "pages.forms.alerts.swal.success.title"
                    ),
                    text: this.$i18n.t("pages.forms.alerts.swal.success.text"),
                    icon: "success",
                    showCancelButton: false,
                    timer: 6000
                }).then(function() {
                    window.location.replace("https://people-genetics.com");
                });
            } catch (e) {
                console.log(e);

                this.alreadySent = false;
                this.buttonText = this.$i18n.t("pages.forms.button-texts.next");
                this.showSpinner = false;

                if (e.response.status === 404) {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.not-exist.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.not-exist.text-ppg"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                    this.trackSampleIdNotFound();
                } else if (e.response.status === 409) {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.form-exist.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.form-exist.text-ppg"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                    this.trackSampleIdAlreadyRegistered();
                } else {
                    this.$swal({
                        title: this.$i18n.t(
                            "pages.forms.alerts.swal.error.other.title"
                        ),
                        text: this.$i18n.t(
                            "pages.forms.alerts.swal.error.other.text-ppg"
                        ),
                        icon: "error",
                        confirmButtonColor: "#DD6B55"
                    });
                    this.trackOtherFail();
                }
            }
        }
    },
    watch: {
        "form.gender": function(newGender, oldGender) {
            this.isFemale = false;

            if (newGender === 0) {
                this.isFemale = true;
                return;
            }
        }
    }
};
</script>

<style scoped></style>
